/* eslint-disable complexity */
import {
  Container,
  SpaceBetween,
  Alert,
  ContentLayout,
  Button,
  Spinner,
  Header,
  Box,
  KeyValuePairs,
  KeyValuePairsProps,
  StatusIndicator,
} from "@amzn/awsui-components-react/polaris";
import React, { ReactNode, useEffect, useState } from "react";
import { TextContent } from "@amzn/awsui-components-react/polaris";
import { RootState } from "src/redux/store";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { resetFormsState } from "src/forms/formsSlice";
import { resetWizardState } from "src/components/wizard/wizardSlice";
import { resetReviewPageState } from "src/forms/reviewPage/reviewPageSlice";
import { resetSitesState } from "src/sites/sitesSlice";
import SimLinkGenerator from "src/components/simLinkGenerator/SimLinkGenerator";
import {
  CLIENT_ERROR_RESPONSES,
  HttpStatusCodes,
  SERVER_ERROR_RESPONSES,
  SUCCESS_RESPONSES,
} from "src/api/types";
import { SIM_TYPES } from "src/components/simLinkGenerator/types";
import { LineOfBusiness, ClaimSubmittedStatus } from "../app/types";
import { CARGO_STAGES } from "../app/Cargo/types";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../app/constants";
import { setValueInAppState } from "../app/appSlice";
import {
  getCargoClaim,
  resetTimeoutWorkaround,
  setValueInCargoAppState,
} from "../app/Cargo/cargoAppSlice";
export interface ClaimSubmittedProps {
  onBackToEdit: any;
  onStartNewClaim: any;
  claimModel: any;
}
const mockSuccessData: KeyValuePairsProps.Pair[] = [
  { label: "Claim number", value: "2J82JR756J07XOUITYVQ1" },
  { label: "Incident Date", value: "02/22/2023" },
  { label: "Claimant Name", value: "Lewis Hamilton" },
  { label: "Incident Site", value: "JFK21" },
  { label: "Reporter Name", value: "Max Verstappen" },
  { label: "Injury Type", value: "Lesion" },
];

const SUCCESS_MESSAGES_CARGO = {
  [CARGO_STAGES.STAGE1]: (
    <Box variant="p">
      Stage one of the submission process is complete. If you are ready to
      provide information concerning the damaged/missing items, please click
      Continue. If you are not ready you will have received an email
      confirmation containing a link which you can use to come back and continue
      with your claim submission
    </Box>
  ),
  [CARGO_STAGES.STAGE2]: (
    <React.Fragment>
      <Box variant="p">
        Thank you for updating your claim with details of the damaged/missing
        items.
      </Box>
      <Box variant="p">
        Stage two of your claim submission is complete. If you are now ready to
        provide your supporting claim documentation to finalize your claim,
        please click Continue. If you do not have your documentation ready you
        can come back and upload later by using the link in the original email
        you received.
      </Box>
      <Box variant="p">
        As a reminder, we will need both the Commercial Invoice and Bill of
        Lading as the minimum documents. It will also be very helpful if you can
        provide as many additional documents as possible, for example: Photos,
        Proff of Delivery/Arrival Receipt, Proof from Carrier/LSP, Police Report
        and/or any other documents.
      </Box>
    </React.Fragment>
  ),
  [CARGO_STAGES.STAGE3]: (
    <React.Fragment>
      <Box variant="p">Thank you for uploading your claim documentation!</Box>
      <Box variant="p">
        Your claim submission is now complete. You will soon be contacted by one
        of our claim experts.
      </Box>
    </React.Fragment>
  ),
};
function getUnknownErrorData(claimModel: any): KeyValuePairsProps.Pair[] {
  return [
    {
      label: "Unknown Error",
      value: (
        <div>
          Oops! Something went wrong while submitting your claim. Please try
          again later. If the issue persists, please open a support{" "}
          <SimLinkGenerator
            target="_blank"
            simType={SIM_TYPES.UNKNOWN_ERROR}
            claimModel={claimModel}
          >
            ticket
          </SimLinkGenerator>
        </div>
      ),
    },
  ];
}

function ClaimSubmitted({
  onBackToEdit,
  onStartNewClaim,
  claimModel,
}: ClaimSubmittedProps) {
  //Redux App state
  const { submitScreenStatus, createWcClaimResponse, lineOfBusiness } =
    useAppSelector((state: RootState) => state.app);
  const cargoSubmitScreenStatus = useAppSelector(
    (state: RootState) => state.cargoApp.submitScreenStatus,
  );
  const {
    createCargoClaimResponse,
    currentStage,
    isTimeoutWorkaround,
    timeoutWorkaroundStartTime,
    claimNumber,
    loadingCargoClaim,
    cargoSubmittedStage,
  } = useAppSelector((state: RootState) => state.cargoApp);
  const iplSubmitScreenStatus = useAppSelector(
    (state: RootState) => state.iplApp.submitScreenStatus,
  );
  const { createIPLClaimResponse, amazonClaimReferenceId } = useAppSelector(
    (state: RootState) => state.iplApp,
  );

  const [submittedData, setSubmittedData] = useState<
    KeyValuePairsProps.Pair[] | undefined
  >(undefined);
  const [claimSubmittedTitleText, setClaimSubmittedTitleText] =
    useState<ReactNode>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const setMessageType = (statusCode: HttpStatusCodes) => {
    if (SERVER_ERROR_RESPONSES.includes(statusCode)) {
      return "error";
    }

    if (CLIENT_ERROR_RESPONSES.includes(statusCode)) {
      return "error";
    }

    if (SUCCESS_RESPONSES.includes(statusCode)) {
      return "success";
    }

    return "custom";
  };
  const response =
    lineOfBusiness === LineOfBusiness.WORKERS_COMPENSATION
      ? createWcClaimResponse
      : lineOfBusiness === LineOfBusiness.CARGO
        ? createCargoClaimResponse
        : createIPLClaimResponse;
  const status =
    lineOfBusiness === LineOfBusiness.WORKERS_COMPENSATION
      ? submitScreenStatus
      : lineOfBusiness === LineOfBusiness.CARGO
        ? cargoSubmitScreenStatus
        : iplSubmitScreenStatus;
  /**
   * useEffect that runs when we receive the reponse from the server, the response is analyzed and formatted
   * accordingly if it has any errors
   */
  useEffect(() => {
    if (!response?.data) return;
    const { statusCode } = response;

    const data: KeyValuePairsProps.Pair[] = [];
    if (statusCode === HttpStatusCodes.GATEWAY_TIMEOUT) {
      setClaimSubmittedTitleText(
        <div>
          Oops! Something went wrong while submitting your claim. You can find
          the error details below
        </div>,
      );
      data.push({
        label: "",
        value: (
          <TextContent>
            <div>
              <div>
                <b>Timeout Error</b>
                <br />
                We are seeing longer than expected delay in receiving a
                confirmation for your claim. Please note that this does not mean
                your claim was not successfully submitted. We kindly request you
                to keep an eye on your email inbox for a confirmation email
                within the next 15 minutes. In the event that you do not receive
                the email, we kindly request you to retry submitting your claim.
                <br />
                If you encounter any further issues, please don&apos;t hesitate
                to contact our{" "}
                <SimLinkGenerator
                  target="_blank"
                  simType={SIM_TYPES.TIMEOUT_ERROR}
                  claimModel={claimModel}
                >
                  Support Team
                </SimLinkGenerator>{" "}
                for assistance.
              </div>
            </div>
          </TextContent>
        ),
      });
    } else if (
      statusCode &&
      SERVER_ERROR_RESPONSES.includes(statusCode as HttpStatusCodes)
    ) {
      setClaimSubmittedTitleText(
        <div>
          Oops! Something went wrong while submitting your claim. Please try
          again later. If the issue persists, please open a support{" "}
          <SimLinkGenerator
            target="_blank"
            simType={SIM_TYPES.UNKNOWN_ERROR}
            claimModel={claimModel}
          >
            ticket
          </SimLinkGenerator>
        </div>,
      );
      data.push({
        label: "",
        value: (
          <TextContent>
            <div>
              {response.data?.length ? (
                <div>
                  <b>
                    Please add the following information to the ticket to better
                    assist you with this issue:
                  </b>
                </div>
              ) : null}
            </div>
          </TextContent>
        ),
      });
    } else {
      setClaimSubmittedTitleText(null);
    }
    const type = setMessageType(statusCode as HttpStatusCodes);

    for (const entry of response.data) {
      data.push({
        label: entry?.fieldName,
        value:
          type === "error" && entry.errorMessage ? (
            <StatusIndicator type={"error"}>
              {entry.errorMessage}
            </StatusIndicator>
          ) : (
            entry.fieldValue
          ),
      });
    }
    setSubmittedData(data);
  }, [response]);
  /**
   * Focus on page header
   */
  useEffect(() => {
    const header = document.querySelector('[title="Claim Submitted Header"]');
    if (header) {
      (header as HTMLElement).tabIndex = 0;
      (header as HTMLElement).focus();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  /**
   * Timeout workaround mechanism for Stage 3 submissions
   * When a timeout occurs, wait 90 seconds then check claim status
   */
  useEffect(() => {
    if (isTimeoutWorkaround && timeoutWorkaroundStartTime && claimNumber) {
      const WAIT_TIME = 90000; // 90 seconds in milliseconds
      const elapsedTime = Date.now() - timeoutWorkaroundStartTime;
      const remainingTime = Math.max(0, WAIT_TIME - elapsedTime);

      const timerId = window.setTimeout(() => {
        // After 90 seconds, check the claim status
        dispatch(getCargoClaim({ claimNumber }));
      }, remainingTime);

      return () => {
        if (timerId) {
          window.clearTimeout(timerId);
        }
      };
    }
  }, [isTimeoutWorkaround, timeoutWorkaroundStartTime, claimNumber, dispatch]);

  /**
   * Handle the result of the timeout workaround getCargoClaim call
   * Check if claim was successfully submitted (STAGE3) or still at STAGE2
   */
  useEffect(() => {
    if (isTimeoutWorkaround && !loadingCargoClaim && cargoSubmittedStage) {
      if (cargoSubmittedStage === "STAGE3") {
        // Claim was successfully submitted!
        dispatch(resetTimeoutWorkaround());
        // Set success status - the existing success rendering logic will handle display
        dispatch(
          setValueInCargoAppState({
            key: "submitScreenStatus",
            value: ClaimSubmittedStatus.SUCCESS,
          }),
        );
        // Build success response data
        const successData: KeyValuePairsProps.Pair[] = [
          {
            label: "Claim Reference Number",
            value: claimNumber,
          },
        ];
        setSubmittedData(successData);
      } else if (cargoSubmittedStage === "STAGE2") {
        // Claim is still at STAGE2, meaning the submission actually failed
        dispatch(resetTimeoutWorkaround());
        dispatch(
          setValueInCargoAppState({
            key: "submitScreenStatus",
            value: ClaimSubmittedStatus.ERROR,
          }),
        );
        // Show timeout error message
        const errorData: KeyValuePairsProps.Pair[] = [
          {
            label: "",
            value: (
              <TextContent>
                <div>
                  <div>
                    <b>Timeout Error</b>
                    <br />
                    We are seeing longer than expected delay in receiving a
                    confirmation for your claim. Unfortunately, after waiting,
                    we were unable to confirm successful submission.
                    <br />
                    Please try submitting your claim again. If you encounter any
                    further issues, please don&apos;t hesitate to contact our{" "}
                    <SimLinkGenerator
                      target="_blank"
                      simType={SIM_TYPES.TIMEOUT_ERROR}
                      claimModel={claimModel}
                    >
                      Support Team
                    </SimLinkGenerator>{" "}
                    for assistance.
                  </div>
                </div>
              </TextContent>
            ),
          },
        ];
        setSubmittedData(errorData);
      }
    }
  }, [
    isTimeoutWorkaround,
    loadingCargoClaim,
    cargoSubmittedStage,
    dispatch,
    claimNumber,
    claimModel,
  ]);
  const resetClaimIntakeAppState = () => {
    dispatch(resetFormsState());
    dispatch(resetWizardState());
    dispatch(resetReviewPageState());
    dispatch(resetSitesState());
    dispatch(
      setValueInAppState({
        key: "claimSubmitSuccess",
        value: true,
      }),
    );
  };
  const renderWarnings = () => {
    if (!response?.warningList?.length) {
      return null;
    }
    const warnings = response.warningList.map((warning, index) => {
      return <li key={index}>{warning}</li>;
    });
    return (
      <Alert
        statusIconAriaLabel="Warning"
        type="warning"
        data-testid="claim-submitted-warning-alert"
        header="Warnings!"
      >
        <div role="alert">
          <TextContent>
            <ul>{warnings}</ul>
          </TextContent>
        </div>
      </Alert>
    );
  };

  function renderContents() {
    if (status === "loading") {
      const isTimeoutWorkaroundActive =
        isTimeoutWorkaround && lineOfBusiness === LineOfBusiness.CARGO;

      return (
        <Box textAlign="center">
          <SpaceBetween size="xxl" direction="vertical">
            <Alert
              statusIconAriaLabel="Info"
              header={
                isTimeoutWorkaroundActive ? "Processing..." : "Loading..."
              }
              data-testid="claim-submitted-loading-alert"
            >
              <span role="alert">
                {isTimeoutWorkaroundActive ? (
                  <>
                    Your claim processing is taking longer than expected. Please
                    do not navigate away from this screen as we continue to
                    process your submission. We are checking the status of your
                    claim and will update you shortly.
                  </>
                ) : (
                  <>
                    Thank you for submitting the claim. Please wait on this
                    screen while we load the claim details. You will also
                    receive a confirmation email shortly.
                  </>
                )}
              </span>
            </Alert>

            <br />
            <Spinner data-testid="claim-submitted-spinner" size="large" />
          </SpaceBetween>
        </Box>
      );
    }
    if (status === "success") {
      resetClaimIntakeAppState();
      const cargoClaimNumber = submittedData?.find(
        (item: KeyValuePairsProps.Pair) =>
          item.label === "Claim Reference Number",
      )?.value;

      // For IPL claims, add Amazon Claim Reference ID to submitted data if not already present
      let displayData = submittedData;
      if (lineOfBusiness === LineOfBusiness.IPL && submittedData) {
        const hasAmazonRefId = submittedData.some(
          (item: KeyValuePairsProps.Pair) =>
            item.label === "Amazon Claim Reference ID",
        );
        if (!hasAmazonRefId && amazonClaimReferenceId) {
          displayData = [
            ...submittedData,
            {
              label: "Amazon Claim Reference ID",
              value: amazonClaimReferenceId,
            },
          ];
        }
      }

      return (
        <Box textAlign="center">
          <SpaceBetween size="xxl" direction="vertical">
            <SpaceBetween size="m" direction="vertical">
              <Alert
                statusIconAriaLabel="Success"
                type="success"
                header="Success!"
                data-testid="claim-submitted-success-alert"
              >
                <span role="alert">
                  Your claim has been successfully submitted! Please see the
                  details below. You will be receiving a confirmation email
                  shortly.
                </span>
              </Alert>
              {renderWarnings()}
            </SpaceBetween>
            <SpaceBetween size="m" direction="vertical">
              <Container
                data-testid={"claim-submitted-success-data-grid"}
                header={
                  <Header variant="h2" data-testid="data-grid-header">
                    Claim Information
                  </Header>
                }
              >
                <KeyValuePairs
                  columns={1}
                  items={
                    !displayData || !displayData.length
                      ? mockSuccessData
                      : displayData
                  }
                />

                {lineOfBusiness === LineOfBusiness.CARGO
                  ? SUCCESS_MESSAGES_CARGO[currentStage]
                  : null}
              </Container>
            </SpaceBetween>

            <Box textAlign="right">
              <span style={{ paddingRight: "5px" }}>
                <Button
                  variant="normal"
                  onClick={onStartNewClaim}
                  data-testid="claim-submitted-start-new-claim-button"
                >
                  Start a New Claim
                </Button>
              </span>
              {cargoClaimNumber &&
              lineOfBusiness === LineOfBusiness.CARGO &&
              currentStage !== CARGO_STAGES.STAGE3 ? (
                <span style={{ paddingLeft: "5px" }}>
                  <Button
                    variant="primary"
                    onClick={() => {
                      onStartNewClaim();
                      navigate(
                        `${ROUTES.CARGO_CLAIM_INTAKE_FORM}?claimNumber=${cargoClaimNumber}`,
                      );
                    }}
                    data-testid="claim-submitted-continue-claim"
                  >
                    Continue
                  </Button>
                </span>
              ) : null}
            </Box>
          </SpaceBetween>
        </Box>
      );
    }
    if (status === "error") {
      const alertText = (
        <div role="alert">
          {claimSubmittedTitleText
            ? claimSubmittedTitleText
            : "Sorry, your claim submission was unsuccessful. Please correct the errors shown below and resubmit the form."}
        </div>
      );
      return (
        <Box textAlign="center">
          <SpaceBetween size="xxl" direction="vertical">
            <Alert
              statusIconAriaLabel="Error"
              type="error"
              header="Error"
              data-testid="claim-submitted-error-alert"
            >
              {alertText}
            </Alert>
            <Container
              data-testid={"claim-submitted-error-data-grid"}
              header={
                <Header variant="h2" data-testid="data-grid-header">
                  Error Details
                </Header>
              }
            >
              <KeyValuePairs
                columns={1}
                items={
                  !submittedData || !submittedData.length
                    ? getUnknownErrorData(claimModel)
                    : submittedData
                }
              />
            </Container>
            <Box textAlign="right">
              <span style={{ paddingRight: "5px" }}>
                <Button
                  onClick={onBackToEdit}
                  data-testid="claim-submitted-back-to-edit-button"
                >
                  Back to edit
                </Button>
              </span>

              <span style={{ paddingLeft: "5px" }}>
                <Button
                  variant="primary"
                  onClick={onStartNewClaim}
                  data-testid="claim-submitted-start-new-claim-button"
                >
                  Start a New Claim
                </Button>
              </span>
            </Box>
          </SpaceBetween>
        </Box>
      );
    }
  }
  const pageTitles = {
    success: "Claim Submitted",
    loading: "Claim Submission in Progress",
    error: "Error Submitting Claim",
  };
  return (
    <React.Fragment>
      <ContentLayout
        header={
          <SpaceBetween size="m">
            <Header variant="h1">
              <div title="Claim Submitted Header">
                {pageTitles[status as keyof typeof pageTitles]}
              </div>
            </Header>
          </SpaceBetween>
        }
      >
        <Container>{renderContents()}</Container>
      </ContentLayout>
      {["success", "loading"].includes(status!) &&
      lineOfBusiness === LineOfBusiness.WORKERS_COMPENSATION ? (
        <Box textAlign="left" padding={{ left: "xs", top: "xs" }}>
          <TextContent>
            If you need to make any updates to the claim, please follow the
            existing process by creating a{" "}
            <SimLinkGenerator
              ariaLabel="Pressing this link will open a new tab where you will be able to create a ticket to modify the submitted claim if necessary."
              target="_blank"
              claimModel={claimModel}
              simType={SIM_TYPES.CLAIM_SUBMITTED}
            >
              ticket.
            </SimLinkGenerator>
            <br />
          </TextContent>
        </Box>
      ) : null}
    </React.Fragment>
  );
}
export default ClaimSubmitted;
